/**
 * Public company profile wizard — entry module.
 *
 * Guards on #iew-form + the iewConfig global, then wires state, enhancement,
 * fields, storage, errors and the REST API into the four-step flow.
 */

import type { IewConfig, ErrorsMap, SubmitResponse } from './wizard/types';
import { LAST_STEP, WizardState } from './wizard/state';
import { WizardApi, SessionExpiredError } from './wizard/api';
import { ErrorRenderer } from './wizard/errors';
import { Enhancer } from './wizard/enhance';
import { Fields } from './wizard/fields';
import { Storage } from './wizard/storage';

function boot($: JQueryStatic, config: IewConfig): void {
  const root = document.getElementById('iew-root');
  const form = document.getElementById('iew-form') as HTMLFormElement | null;
  if (!root || !form) {
    return;
  }

  // Dev preview: loading the page with #iew-success in the URL jumps straight to
  // the success panel (same swap as a real submit) so the success screen can be
  // styled without completing a submission. Guarded by the hash, so it never
  // fires for ordinary visitors.
  if (window.location.hash === '#iew-success') {
    showSuccess(root, form);
    return;
  }

  const state = new WizardState(root);
  const api = new WizardApi(config);
  const errors = new ErrorRenderer(root, config);
  const enhancer = new Enhancer(root, config, $);
  const fields = new Fields(root, config, enhancer);
  const storage = new Storage(root, enhancer);

  fields.init();
  enhancer.initStep(1);

  // Persist on any input/change and enhance a panel the first time it shows.
  state.onStepChange((step) => {
    enhancer.initStep(step);
    storage.save(step);
  });
  form.addEventListener('input', () => storage.scheduleSave(state.currentStep));
  form.addEventListener('change', () => storage.scheduleSave(state.currentStep));

  // Restore a saved draft (files excluded → note near the file pills).
  const draft = storage.load();
  if (draft) {
    storage.restore(draft, $);
    // restore() may inject extra repeater rows outside fields.init()'s pass —
    // re-bind their remove buttons and refresh × visibility.
    fields.syncRepeaters();
    showRestoreNotice(root, config, storage);
    if (draft.step >= 1 && draft.step <= LAST_STEP) {
      state.goTo(draft.step);
    }
  }

  const next = form.querySelector<HTMLButtonElement>('[data-iew="next"]');
  const prev = form.querySelector<HTMLButtonElement>('[data-iew="prev"]');
  const submitBtn = form.querySelector<HTMLButtonElement>('[data-iew="submit"]');

  next?.addEventListener('click', () => {
    void advance(state, api, fields, errors, config);
  });

  prev?.addEventListener('click', () => {
    if (state.currentStep > 1) {
      state.goTo(state.currentStep - 1);
    }
  });

  // Stepper backward navigation to visited steps only.
  root.querySelectorAll<HTMLElement>('.iew-stepper__step[data-step]').forEach((el) => {
    el.addEventListener('click', () => {
      const target = Number(el.dataset.step);
      if (target < state.currentStep && state.canVisit(target)) {
        state.goTo(target);
      }
    });
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void finish(state, api, fields, errors, storage, root, form, submitBtn, config);
  });
}

/** Next: validate the current step, then advance on success. */
async function advance(
  state: WizardState,
  api: WizardApi,
  fields: Fields,
  errors: ErrorRenderer,
  config: IewConfig,
): Promise<void> {
  const step = state.currentStep;
  const { values, filesPresent } = fields.collectStep(step);
  errors.clear(step);

  try {
    const res = await api.validateStep(step, values, filesPresent);
    if (!('valid' in res)) {
      // Only the rate_limited shape lacks `valid`.
      errors.notice(res.message);
      return;
    }
    if (res.valid) {
      state.markDone(step);
      state.goTo(step + 1);
      return;
    }
    errors.render(res.errors, res.code === 'duplicate_fiscal_code');
  } catch (err) {
    handleTransportError(err, errors, config);
  }
}

/** Submit (last step): validate, then POST the multipart form. */
async function finish(
  state: WizardState,
  api: WizardApi,
  fields: Fields,
  errors: ErrorRenderer,
  storage: Storage,
  root: HTMLElement,
  form: HTMLFormElement,
  submitBtn: HTMLButtonElement | null,
  config: IewConfig,
): Promise<void> {
  errors.clear(LAST_STEP);
  const { values, filesPresent } = fields.collectStep(LAST_STEP);

  try {
    const stepRes = await api.validateStep(LAST_STEP, values, filesPresent);
    if (!('valid' in stepRes)) {
      // rate_limited shape.
      errors.notice(stepRes.message);
      return;
    }
    if (!stepRes.valid) {
      errors.render(stepRes.errors, stepRes.code === 'duplicate_fiscal_code');
      return;
    }

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
    }

    const res: SubmitResponse = await api.submitForm(form);

    if ('success' in res && res.success) {
      storage.clear();
      showSuccess(root, form);
      return;
    }

    handleSubmitFailure(res, state, errors);
  } catch (err) {
    handleTransportError(err, errors, config);
  } finally {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.classList.remove('is-loading');
    }
  }
}

function handleSubmitFailure(res: SubmitResponse, state: WizardState, errors: ErrorRenderer): void {
  if ('code' in res) {
    switch (res.code) {
      case 'validation_failed':
      case 'upload_failed':
      case 'duplicate_fiscal_code':
        if ('first_error_step' in res && res.first_error_step) {
          state.goTo(res.first_error_step);
        }
        if ('errors' in res && res.errors) {
          errors.render(res.errors as ErrorsMap, res.code === 'duplicate_fiscal_code');
        }
        return;
      case 'rate_limited':
      case 'spam_detected':
      case 'server_error':
      default:
        errors.notice(res.message ?? '');
    }
  }
}

function handleTransportError(err: unknown, errors: ErrorRenderer, config: IewConfig): void {
  if (err instanceof SessionExpiredError) {
    errors.notice(config.i18n.sessionExpired ?? '', 'error', true);
  } else {
    errors.notice(config.i18n.networkError ?? '');
  }
}

function showSuccess(root: HTMLElement, form: HTMLFormElement): void {
  form.hidden = true;
  const stepper = root.querySelector<HTMLElement>('.iew-stepper');
  if (stepper) {
    stepper.hidden = true;
  }
  const success = root.querySelector<HTMLElement>('.iew-success');
  if (success) {
    success.hidden = false;
  }
  window.scrollTo({ top: root.getBoundingClientRect().top + window.scrollY - 20, behavior: 'smooth' });
}

function showRestoreNotice(root: HTMLElement, config: IewConfig, storage: Storage): void {
  const form = root.querySelector<HTMLElement>('#iew-form');
  if (!form || form.querySelector('.iew-restore')) {
    return;
  }
  const box = document.createElement('div');
  box.className = 'iew-restore';
  box.textContent = config.i18n.restoredNotice ?? '';

  const discard = document.createElement('button');
  discard.type = 'button';
  discard.className = 'iew-restore__discard';
  discard.textContent = config.i18n.discardDraft ?? '';
  discard.addEventListener('click', () => {
    storage.clear();
    window.location.reload();
  });

  box.appendChild(document.createTextNode(' '));
  box.appendChild(discard);
  form.insertBefore(box, form.firstChild);
}

const jq = window.jQuery;
const cfg = window.iewConfig;
if (jq && cfg && document.getElementById('iew-form')) {
  jq(() => boot(jq, cfg));
}
